import { ChevronLeft, ChevronRight } from "lucide-react"
import { useMemo, useState } from "react"
import { cx } from "../lib/cx"
import type { Task } from "../types"

type Props = {
  tasks: Task[]
  onTaskClick: (task: Task) => void
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

const priorityDot: Record<string, string> = {
  high: "bg-rose-400",
  medium: "bg-yellow-400",
  low: "bg-slate-400",
}

const CalendarView = ({ tasks, onTaskClick }: Props) => {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const prev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1) }
    else setMonth(month - 1)
  }
  const next = () => {
    if (month === 11) { setMonth(0); setYear(year + 1) }
    else setMonth(month + 1)
  }

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days: Array<{ date: number; key: string } | null> = []

    // Leading blanks
    for (let i = 0; i < firstDay; i++) days.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      days.push({ date: d, key })
    }
    // Trailing blanks to fill 6 rows
    while (days.length < 42) days.push(null)
    return days
  }, [year, month])

  // Group tasks by due date
  const tasksByDate = useMemo(() => {
    const map: Record<string, Task[]> = {}
    for (const task of tasks) {
      if (task.dueDate) {
        const dateKey = task.dueDate.split("T")[0]
        if (!map[dateKey]) map[dateKey] = []
        map[dateKey].push(task)
      }
    }
    return map
  }, [tasks])

  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`

  const tasksForSelected = selectedDay ? (tasksByDate[selectedDay] || []) : []
  const tasksWithoutDate = tasks.filter((t) => !t.dueDate)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">
          {MONTHS[month]} {year}
        </h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={prev}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone hover:bg-slate-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()) }}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-stone hover:bg-slate-100"
          >
            Today
          </button>
          <button
            type="button"
            onClick={next}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone hover:bg-slate-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px">
        {DAYS.map((day) => (
          <div key={day} className="py-2 text-center text-xs font-medium text-stone">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px rounded-xl border border-line bg-line overflow-hidden">
        {calendarDays.map((day, i) => {
          if (!day) {
            return <div key={`blank-${i}`} className="min-h-[80px] bg-slate-50" />
          }
          const dayTasks = tasksByDate[day.key] || []
          const isToday = day.key === todayKey
          const isSelected = day.key === selectedDay

          return (
            <button
              type="button"
              key={day.key}
              onClick={() => setSelectedDay(isSelected ? null : day.key)}
              className={cx(
                "min-h-[80px] bg-white p-1.5 text-left transition hover:bg-moss/5",
                isSelected && "bg-moss/10",
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cx(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium",
                    isToday ? "bg-moss text-white" : "text-slate-700",
                  )}
                >
                  {day.date}
                </span>
                {dayTasks.length > 0 && (
                  <span className="text-xs text-stone">{dayTasks.length}</span>
                )}
              </div>
              {dayTasks.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {dayTasks.slice(0, 3).map((t) => (
                    <div
                      key={t.id}
                      className={cx("h-1.5 w-1.5 rounded-full", priorityDot[t.priority] || "bg-slate-400")}
                      title={t.title}
                    />
                  ))}
                  {dayTasks.length > 3 && (
                    <span className="text-xs text-stone">+{dayTasks.length - 3}</span>
                  )}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Selected day panel */}
      {selectedDay && (
        <div className="rounded-xl border border-line bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">
            Tasks for {new Date(selectedDay + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </h3>
          {tasksForSelected.length > 0 ? (
            <div className="space-y-2">
              {tasksForSelected.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => onTaskClick(t)}
                  className="flex w-full items-center justify-between rounded-lg border border-line bg-white p-3 text-left transition hover:shadow-sm"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">{t.title}</p>
                    {t.project && <p className="text-xs text-stone">{t.project}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cx(
                      "rounded-full px-2 py-0.5 text-xs font-medium",
                      t.priority === "high" ? "bg-rose-100 text-rose-700" :
                      t.priority === "medium" ? "bg-yellow-100 text-yellow-700" :
                      "bg-slate-100 text-slate-700",
                    )}>
                      {t.priority}
                    </span>
                    <span className={cx(
                      "rounded-full px-2 py-0.5 text-xs font-medium",
                      t.status === "done" ? "bg-green-100 text-green-700" :
                      t.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                      t.status === "review" ? "bg-yellow-100 text-yellow-700" :
                      "bg-slate-100 text-slate-700",
                    )}>
                      {t.status.replace("_", " ")}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone">No tasks due this day</p>
          )}
        </div>
      )}

      {/* Tasks without due date */}
      {tasksWithoutDate.length > 0 && (
        <div className="rounded-xl border border-line bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">
            No Due Date ({tasksWithoutDate.length})
          </h3>
          <div className="space-y-2">
            {tasksWithoutDate.slice(0, 10).map((t) => (
              <button
                type="button"
                key={t.id}
                onClick={() => onTaskClick(t)}
                className="flex w-full items-center justify-between rounded-lg border border-line bg-white p-2.5 text-left transition hover:shadow-sm"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{t.title}</p>
                  {t.project && <p className="text-xs text-stone">{t.project}</p>}
                </div>
                <span className={cx(
                  "rounded-full px-2 py-0.5 text-xs font-medium",
                  t.priority === "high" ? "bg-rose-100 text-rose-700" :
                  t.priority === "medium" ? "bg-yellow-100 text-yellow-700" :
                  "bg-slate-100 text-slate-700",
                )}>
                  {t.priority}
                </span>
              </button>
            ))}
            {tasksWithoutDate.length > 10 && (
              <p className="text-xs text-stone text-center">
                +{tasksWithoutDate.length - 10} more
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CalendarView
